/**
 * TimeSavedLine - one item's "usually X h by hand → PONS took Y → saved Z ≈
 * money" (F-TIME, R4-14 / R4-15).
 *
 * The PONS time is MEASURED: how long PONS actually spent processing this
 * item. The by-hand time is how long such work USUALLY takes - the firm's
 * figure for the kind or type of work, unless someone who knows better sets
 * this item's own. Anyone with edit access can do that here, and put it back
 * to the firm's figure.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiArrowRightLight, PiClockLight, PiPencilSimpleLight } from 'react-icons/pi';
import { useAppDispatch } from '../../store';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import Button from './TailwindComponents/Button';
import Input from './TailwindComponents/Input';
import { formatCurrency } from '../../utils/formatCurrency';
import {
  TimeSavedItem, TimeSavedItemKind, TimeSavedSource, formatDuration, formatHours, getTimeSavedItem, setTimeSavedItem,
} from '../../utils/timeSavedApi';

const SOURCE_LABEL: Record<TimeSavedSource, string> = {
  item: 'Set for this item',
  firm_type: 'Your firm’s estimate for this type',
  firm_kind: 'Your firm’s estimate',
  default_type: 'Typical for this type',
  default: 'Estimated from its size',
};

interface Props {
  kind: TimeSavedItemKind;
  uuid: string;
  className?: string;
}

const TimeSavedLine: React.FC<Props> = ({ kind, uuid, className = '' }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [data, setData] = useState<TimeSavedItem | null>(null);
  const [editing, setEditing] = useState(false);
  const [manual, setManual] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setData(null);
    getTimeSavedItem(kind, uuid)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { /* no line rather than an error: it is a side figure */ });
    return () => { alive = false; };
  }, [kind, uuid]);

  const startEdit = () => {
    if (!data) return;
    setManual(String(data.manual_hours));
    setEditing(true);
  };

  const save = useCallback(async (m: number | null) => {
    setSaving(true);
    try {
      setData(await setTimeSavedItem(kind, uuid, m));
      setEditing(false);
    } catch (e: any) {
      dispatch(showFeedbackModal({
        showModal: true, modalType: 'error', duration: 5000,
        message: e?.response?.data?.error || t('Could not save the hours.'),
      }));
    } finally {
      setSaving(false);
    }
  }, [kind, uuid, dispatch, t]);

  if (!data) return null;

  if (editing) {
    const m = Number(manual);
    const valid = manual !== '' && m >= 0;
    return (
      <div className={`space-y-2 ${className}`} data-testid="time-saved-edit">
        <div className="max-w-[14rem]">
          <Input type="number" id={`ts-manual-${uuid}`} label={t('Usually takes by hand (hours)')} value={manual} min={0}
                 onChange={(e) => setManual(e.target.value)}
                 description={t('PONS’s time is measured, so only this is set.')} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="primary" size="small" onClick={() => save(m)} disabled={!valid} isLoading={saving}>
            {t('Save')}
          </Button>
          <Button variant="outline" size="small" onClick={() => setEditing(false)}>{t('Cancel')}</Button>
          {data.source === 'item' && (
            <Button variant="neutral" size="small" onClick={() => save(null)} disabled={saving}>
              {t('Use the firm’s estimate')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  const measured = data.pons_source !== 'estimated';
  return (
    <div className={`text-sm ${className}`} data-testid="time-saved-line">
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-fg-body">
        <PiClockLight className="h-4 w-4 text-fg-muted" aria-hidden />
        <span title={t('Usually takes by hand')}>{t('Usually {{h}} by hand', { h: formatHours(data.manual_hours) })}</span>
        <PiArrowRightLight className="h-3 w-3 text-fg-muted rtl:rotate-180" aria-hidden />
        <span title={measured ? t('How long PONS spent processing it') : t('Estimated - this item has no processing time recorded')}>
          {measured
            ? t('PONS took {{d}}', { d: formatDuration(data.pons_hours, t) })
            : t('PONS about {{d}}', { d: formatDuration(data.pons_hours, t) })}
        </span>
        <PiArrowRightLight className="h-3 w-3 text-fg-muted rtl:rotate-180" aria-hidden />
        <span className="font-medium text-success-700 dark:text-success-300">
          {t('{{hours}} saved', { hours: formatHours(data.saved_hours) })}
        </span>
        <span className="text-fg-muted">≈ {formatCurrency(data.value_saved, data.currency)}</span>
        {data.can_edit && (
          <button
            type="button"
            onClick={startEdit}
            className="ms-1 inline-flex items-center gap-1 text-xs text-fg-muted hover:text-fg-strong"
            aria-label={t('Change how long this usually takes')}
          >
            <PiPencilSimpleLight className="h-3.5 w-3.5" aria-hidden />
            {t('Edit')}
          </button>
        )}
      </div>
      <p className="mt-0.5 text-[11px] text-fg-muted">
        {t(SOURCE_LABEL[data.source])}
        {data.type_name ? ` · ${data.type_name}` : ''}
      </p>
    </div>
  );
};

export default TimeSavedLine;
